import { FirmarkSeal } from "./FirmarkSeal";

type Stage = { n: string; title: string; desc: string };

const STAGES: Stage[] = [
  {
    n: "1",
    title: "Add a Site & floor plan",
    desc: "Pin the building, confirm site conditions, and drop in a master floor plan or a custom imported architectural plan.",
  },
  {
    n: "2",
    title: "Set your standards",
    desc: "Choose exposure, snow, materials, and your firm's defaults once, then save them as reusable design profiles.",
  },
  {
    n: "3",
    title: "Calcs auto-generate",
    desc: "Loads resolve roof-down to code; every member is sized and checked against its governing clause.",
  },
  {
    n: "4",
    title: "AI supervises",
    desc: "It reviews the run, flags the governing case, asks when a call is judgment, and learns your preferences. Each one lands in the log.",
  },
  {
    n: "5",
    title: "Your PE reviews & stamps",
    desc: "A licensed engineer walks the auditable package, confirms or overrides, and applies the stamp. AI never does.",
  },
];

type LogEntry = { kind: "asked" | "assumed" | "learned"; text: string; clause: string };

const LOG: LogEntry[] = [
  { kind: "asked", text: "Two load paths within 3%: which governs the ridge?", clause: "Flagged for review" },
  { kind: "assumed", text: "Roof live load 20 psf (no snow at this site)", clause: "ASCE 7-22 §4.3" },
  { kind: "learned", text: "Default to glulam ridge beams for this firm", clause: "From your last 6 projects" },
  { kind: "assumed", text: "Wind exposure C · suburban terrain", clause: "ASCE 7-22 §26.7" },
];

const LOG_LABEL: Record<LogEntry["kind"], string> = {
  asked: "Asked",
  assumed: "Assumed",
  learned: "Learned",
};

export function Transparency() {
  return (
    <section className="landing-wrap landing-wrap--wide landing-section" id="how-it-works">
      <div className="section-head">
        <span className="section-eyebrow">How it works</span>
        <h2 className="section-title">See every step. Defend every number.</h2>
        <p className="section-sub">
          From a floor plan to a stamped set, the whole workflow is in the open: the inputs you
          steer, the standards you set, and every clause the engine cites. The proprietary solver
          stays sealed; the reasoning never does.
        </p>
      </div>

      {/* The 5-stage spine */}
      <ol className="spine" aria-label="Workflow stages">
        {STAGES.map((s, i) => (
          <li className="spine-stage" key={s.n}>
            <div className="spine-rail" aria-hidden="true">
              <span className="spine-node">{s.n}</span>
              {i < STAGES.length - 1 && <span className="spine-line" />}
            </div>
            <div className="spine-body">
              <h3 className="spine-title">{s.title}</h3>
              <p className="spine-desc">{s.desc}</p>
            </div>
          </li>
        ))}
      </ol>

      {/* Decision log — the unique wedge */}
      <div className="trans-split">
        <div className="decision-log">
          <div className="decision-log-head">
            <div>
              <h3 className="decision-log-title">Decision log</h3>
              <p className="decision-log-sub">Every question, assumption, and learned preference, each tied to a clause.</p>
            </div>
            <span className="sample-pill">Sample</span>
          </div>
          <ul className="decision-log-list">
            {LOG.map((e, i) => (
              <li className={`log-row log-row--${e.kind}`} key={i}>
                <span className="log-kind">{LOG_LABEL[e.kind]}</span>
                <span className="log-text">{e.text}</span>
                <span className="log-clause">{e.clause}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="proprietary-card">
          <div className="proprietary-seal" aria-hidden="true">
            <FirmarkSeal size={72} color="var(--fm-spruce)" tickRing={false} />
          </div>
          <h3 className="proprietary-title">What you see vs. what stays sealed</h3>
          <div className="proprietary-cols">
            <div>
              <p className="prop-col-label">You see</p>
              <ul className="prop-list prop-list--see">
                <li>Every input and the standards you set</li>
                <li>Each cited clause and edition</li>
                <li>The full decision log, exportable</li>
                <li>Governing case, flagged</li>
              </ul>
            </div>
            <div>
              <p className="prop-col-label">Stays proprietary</p>
              <ul className="prop-list prop-list--hide">
                <li>The solver internals</li>
                <li>The sizing search heuristics</li>
                <li>The model weights</li>
              </ul>
            </div>
          </div>
          <p className="proprietary-note">Name the signals, hide the math. Trust comes from the trail, not the kernel.</p>
        </div>
      </div>
    </section>
  );
}
